/**
 * The parties feature's OWN write surfaces — `POST /countries/:cc/parties`
 * (WEB TIER A · WT6, the `parties/new` page) and `POST /parties/:id/referral`
 * (the party record's referral control, OQ-PARTY-REFERRAL-WRITE ruled
 * 2026-08-18).
 *
 * ===========================================================================
 * WHY A SECOND MODULE WHEN `features/inquiries/api.ts` ALREADY HAS A createParty
 * ===========================================================================
 * That one was the DELETED New Inquiry panel's inline-prospect call and has had
 * no consumer since WT5 killed the slide-over; it also predates migration 16's
 * `customer_country` column, which the international scope (XM) REQUIRES at
 * create time (`parties.service.ts` `resolveCustomerCountry` — a refusal, not a
 * default). The page that owns customer creation now lives in THIS feature, so
 * its body type lives here, transcribed complete from `CreatePartyDto`
 * (`apps/api/src/modules/parties/dto/parties.dto.ts:22`). The dead inquiries
 * pair is FILED for retirement (ledger item 16), not folded into this cluster.
 *
 * `PartyDto` is imported, not re-declared: the shape both write surfaces get
 * back is the one the pipeline already transcribed, and two copies would drift.
 */

import { apiRequest } from '../../lib/api';
import type { PartyDto } from '../inquiries/api';

export type { PartyDto };
/*
 * Re-exported rather than copied, the `inquiryKeys` precedent in `crfs/api.ts`:
 * `fetchParty` backs the inquiry record's Customer panel through the SAME
 * `partyKeys.record(id)` cache entry the party record page reads and the
 * referral control writes into — a second copy of either would mean a save on
 * the record page leaving a stale customer panel behind it.
 */
export { fetchParty, partyKeys } from '../inquiries/api';

/** `CreatePartyDto`, complete. Every optional is OMITTED when not stated — the
 * DTO treats '' as a value, and absence is the only honest "not stated". */
export interface CreatePartyBody {
  name: string;
  address?: string;
  city?: string;
  state?: string;
  /** GSTIN / TRN / VAT / NIF — the country's own `taxLabel` word. Stored verbatim. */
  tax_no?: string;
  /**
   * Where the CUSTOMER is (§B3, migration 16). REQUIRED by the service for the
   * international scope (XM) and refused as a default; optional-and-usually-null
   * for the five domestic scopes, where the selling country answers it.
   */
  customer_country?: string;
  phone?: string;
  email?: string;
  /**
   * WHO SENT US THIS CUSTOMER — `CreatePartyDto.referral` (`parties.dto.ts`, migration 49;
   * owner ruling R4 *"capture them, never require them"*). Free text, at most 200
   * characters; the SERVER trims it and turns blank into NULL, and otherwise stores it AS
   * TYPED (`parties/referral.ts`, the ONE rule shared with the CRF column). It re-cases
   * nothing — the `direct` → `Direct` fold this line used to describe was struck by the
   * owner on 2026-08-18. OPTIONAL and OMITTED WHEN BLANK like every other optional here — the DTO
   * treats `''` as a value and the service turns it into NULL anyway, but absence is the
   * only honest spelling of "not stated" on the wire.
   *
   * ⚠ NO LONGER CREATE-ONLY — RE-MEASURED. This paragraph used to record that E22's
   * `POST /parties/:id/referral` was a 501 stub blocked on OQ-PARTY-REFERRAL-WRITE, so a
   * customer that already existed had no referral write surface. The owner ruled on
   * 2026-08-18 (*"parties.create-gated, single-column route, its own verb"*), the route
   * now writes, and the party RECORD page (`PartyRecordPage`) offers it through
   * `setPartyReferral` below. THIS field remains the CREATE capture only; the create
   * page still cannot edit an existing customer, and does not pretend to.
   */
  referral?: string;
}

export async function createParty(
  countryCode: string,
  body: CreatePartyBody,
): Promise<PartyDto> {
  return apiRequest<PartyDto>(`/countries/${countryCode}/parties`, { method: 'POST', body });
}

/**
 * `SetPartyReferralDto`, complete. The key is MANDATORY on the route (`{}` is a
 * 400 — `@ValidateIf` makes `undefined` unreachable), so both statements are
 * spelled out rather than left to omission: a string SETS, `null` CLEARS.
 */
export interface SetPartyReferralBody {
  referral: string | null;
}

/**
 * THE DOOR ONTO `parties.referral` THAT OPENS AFTER THE CUSTOMER EXISTS —
 * `POST /parties/:id/referral` (OQ-PARTY-REFERRAL-WRITE, ruled 2026-08-18).
 * Gated `parties.create` at the server (*"whoever may bring a customer into the
 * system may record who sent them"*); `@CountryScoped(Party)` answers 404 for an
 * absent row and 403 for one outside the caller's countries, before the handler
 * runs.
 *
 * 200, NOT 201 — nothing is created; one column is set on a row that already
 * existed. The response is the WHOLE party as the server now holds it
 * (normalised: trimmed, blank → NULL, and NOTHING re-cased — the owner struck
 * the `direct` → `Direct` fold on 2026-08-18), so the caller puts it straight
 * into `partyKeys.record(id)` and the screen shows the stored value the moment
 * the form closes.
 *
 * IT OVERWRITES (200 on a second write, never 409) and the trail pays for that:
 * every write emits one `party.referral_set` whose payload carries
 * `previous_referral` — the value the write destroyed, EXPLICITLY null on a
 * first set (owner ruling 2026-08-20: *"KEEP the overwrite, and make the trail
 * carry the OLD VALUE as well as who changed it"*). The confirm on the record
 * page shows that value BEFORE the write, so the operator reads what the trail
 * is about to record. `apps/api/test/referral-write.e2e-spec.ts` §(b) and
 * `party-referral-screen.e2e-spec.ts` hold all of this against the real API.
 */
export async function setPartyReferral(
  id: string,
  body: SetPartyReferralBody,
): Promise<PartyDto> {
  return apiRequest<PartyDto>(`/parties/${id}/referral`, { method: 'POST', body });
}

/**
 * `UpdatePartyDto` (design §3 U3, D13) — SAF-T (AO) countries only; the server refuses others 422.
 * Absent = leave; for the nullable fields `null` clears. The AGT locks answer 409 with a
 * sentence the page shows as it is (`partyErrorText` passes 409 through). Phone and state since
 * U6 (the SAF-T (AO) export refuses them over the XSD's bounds).
 */
export interface UpdatePartyBody {
  name?: string;
  tax_no?: string | null;
  address?: string | null;
  city?: string | null;
  phone?: string | null;
  state?: string | null;
}

/** `PATCH /parties/:id` — gated `parties.create`; answers the whole party as stored. */
export async function updateParty(id: string, body: UpdatePartyBody): Promise<PartyDto> {
  return apiRequest<PartyDto>(`/parties/${id}`, { method: 'PATCH', body });
}
